/* Guitar tuner model: turns raw pitch frames into a steady, readable tuner display.

   Why: raw pitch frames are jumpy. A plucked string starts sharp, its clarity dips as it decays,
   harmonics cause octave errors and the signal fades below the gate long before the string stops.
   Showing frames directly makes the needle flicker, swing back to the centre (which looks "in tune")
   and lose the string. This model:
   - skips the sharp attack right after each pluck,
   - takes the median of the last ~0.25 s (outliers and octave slips cannot move it),
   - smooths the needle with a short time constant,
   - locks onto a string and only switches after consistent evidence (auto mode),
   - folds octave errors back onto the locked string,
   - holds the last reading for a few seconds after the sound fades ("held"),
   - confirms "in tune" only after the pitch stays in the zone for a while (with hysteresis). */
import { median } from '../dsp/math';

/** Open strings, index 0 = 6th string (low E) ... 5 = 1st string. */
const OPEN = [40, 45, 50, 55, 59, 64];
const openOf = (string: number): number => OPEN[6 - string] as number;

export interface TunerFrame {
  /** seconds */
  t: number;
  /** Hz, 0 when unvoiced */
  freq: number;
  clarity: number;
  rms: number;
  /** level threshold: quieter frames count as silence */
  gate: number;
  /** the app itself is playing (reference tone); ignore the input */
  gated: boolean;
  /** pluck detector level (see dsp hfLevel) */
  hf: number;
}

export interface TunerOptions {
  a4: number;
  /** chosen string 1..6, or null to detect the string automatically */
  string: number | null;
  /** in-tune zone (cents) */
  tolCents: number;
  /** leave the in-tune state only beyond this (cents) */
  exitCents: number;
  /** keep the last reading on screen this long after the sound fades (s) */
  holdSec: number;
  /** ignore pitch for this long after a pluck (s) */
  attackSkipSec: number;
  /** median window (s) */
  windowSec: number;
  /** needle smoothing time constant (s) */
  smoothSec: number;
  /** another string must dominate roughly this much recent signal before the tuner switches (s) */
  switchSec: number;
  /** time in the zone before a string counts as tuned (s) */
  confirmSec: number;
  minClarity: number;
}

export const DEFAULT_TUNER: TunerOptions = {
  a4: 440,
  string: null,
  tolCents: 5,
  exitCents: 8,
  holdSec: 4,
  attackSkipSec: 0.06,
  windowSec: 0.25,
  smoothSec: 0.12,
  switchSec: 0.18,
  confirmSec: 0.5,
  minClarity: 0.85,
};

export type TunerState = 'idle' | 'live' | 'held';

export interface TunerReading {
  state: TunerState;
  /** 1..6 when tuning a string; null for a note that is not near any open string */
  string: number | null;
  /** target MIDI note (the open string, or the nearest semitone off-string) */
  target: number | null;
  offString: boolean;
  /** smoothed deviation from the target in cents */
  cents: number | null;
  hz: number | null;
  inTune: boolean;
  /** 0..1 progress towards confirming the string as tuned */
  confirm: number;
  /** string number confirmed in tune on this frame, otherwise null */
  confirmed: number | null;
}

interface Lock {
  key: string;
  string: number | null;
  target: number;
}

export class Tuner {
  o: TunerOptions;
  private state: TunerState = 'idle';
  private lock: Lock | null = null;
  /** recent valid frames as string candidates; the lock follows their majority */
  private votes: { t: number; lock: Lock; c: number }[] = [];
  private buf: { t: number; c: number }[] = [];
  private display: number | null = null;
  private lastT: number | null = null;
  /** last frame that updated (or confirmed) the display */
  private lastValid = -Infinity;
  /** last frame with any usable pitch */
  private lastPitch = -Infinity;
  private lastOnset = -Infinity;
  private env = 0;
  private inTune = false;
  private confirmAcc = 0;
  private confirmedKey: string | null = null;

  constructor(opts: Partial<TunerOptions> = {}) {
    this.o = { ...DEFAULT_TUNER, ...opts };
  }

  setOptions(opts: Partial<TunerOptions>): void {
    const stringChanged = opts.string !== undefined && opts.string !== this.o.string;
    this.o = { ...this.o, ...opts };
    if (stringChanged) this.reset();
  }

  reset(): void {
    this.state = 'idle';
    this.lock = null;
    this.votes = [];
    this.buf = [];
    this.display = null;
    this.inTune = false;
    this.confirmAcc = 0;
    this.confirmedKey = null;
  }

  /** Candidate string and deviation for a fractional MIDI note. `fresh`: a new pluck after silence. */
  private candidate(mf: number, fresh: boolean): { lock: Lock; cents: number } {
    const o = this.o;
    if (o.string != null) {
      const target = openOf(o.string);
      let c = (mf - target) * 100;
      if (Math.abs(c) > 600) c -= Math.round(c / 1200) * 1200;
      return { lock: { key: `s${o.string}`, string: o.string, target }, cents: c };
    }
    // octave slip on the string we are already tuning (strong 2nd harmonic, subharmonic)
    if (!fresh && this.lock && this.lock.string != null) {
      const T = this.lock.target;
      for (const k of [12, -12]) {
        if (Math.abs(mf - (T + k)) <= 0.6) return { lock: this.lock, cents: (mf - k - T) * 100 };
      }
    }
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < 6; i++) {
      const d = Math.abs(mf - (OPEN[i] as number));
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (bestD > 4) {
      const target = Math.round(mf);
      return { lock: { key: `n${target}`, string: null, target }, cents: (mf - target) * 100 };
    }
    const string = 6 - best;
    const target = OPEN[best] as number;
    return { lock: { key: `s${string}`, string, target }, cents: (mf - target) * 100 };
  }

  /** Candidate with the most votes in the recent window, with its vote count. */
  private majority(): { lock: Lock; n: number } | null {
    const counts = new Map<string, { lock: Lock; n: number }>();
    for (const v of this.votes) {
      const e = counts.get(v.lock.key);
      if (e) e.n++;
      else counts.set(v.lock.key, { lock: v.lock, n: 1 });
    }
    let best: { lock: Lock; n: number } | null = null;
    for (const e of counts.values()) if (!best || e.n > best.n) best = e;
    return best;
  }

  push(f: TunerFrame): TunerReading {
    const o = this.o;
    const dt = this.lastT == null ? 0 : Math.min(0.1, Math.max(0, f.t - this.lastT));
    this.lastT = f.t;
    let confirmed: number | null = null;

    // pluck onsets: sharp rise of the high-frequency level over its decaying envelope
    const prevEnv = this.env;
    this.env = Math.max(f.hf, this.env * 0.5 ** (dt / 0.12));
    if (!f.gated && f.hf > prevEnv * 1.8 && f.rms >= f.gate && f.t - this.lastOnset > 0.12) {
      this.lastOnset = f.t;
      this.buf = [];
    }

    const valid =
      !f.gated && f.freq > 0 && f.clarity >= o.minClarity && f.rms >= f.gate && f.t - this.lastOnset >= o.attackSkipSec;

    if (valid) {
      const mf = 69 + 12 * Math.log2(f.freq / o.a4);
      // after a pause the next pluck may be another string: start a new vote
      const fresh = f.t - this.lastPitch > 0.6;
      this.lastPitch = f.t;
      if (fresh) this.votes = [];
      const cand = this.candidate(mf, fresh);
      this.votes.push({ t: f.t, lock: cand.lock, c: cand.cents });
      while (this.votes.length && (this.votes[0] as { t: number }).t < f.t - o.switchSec * 1.7) this.votes.shift();

      // Lock onto a string only with a few agreeing frames, and switch only when another string
      // clearly dominates the recent frames, so single glitches (attack, octave slips) never win.
      const top = this.majority();
      let snap = false;
      if (top && top.lock.key !== this.lock?.key) {
        const need = this.lock ? Math.max(6, Math.ceil(this.votes.length * 0.6)) : 3;
        if (top.n >= need) {
          this.relock(top.lock);
          snap = true;
        }
      }

      if (this.lock && cand.lock.key === this.lock.key) {
        this.buf.push({ t: f.t, c: cand.cents });
        while (this.buf.length && (this.buf[0] as { t: number }).t < f.t - o.windowSec) this.buf.shift();
        const med = median(this.buf.map((b) => b.c));
        if (this.display == null || snap) this.display = med;
        else this.display += (med - this.display) * (1 - Math.exp(-dt / o.smoothSec));
        this.state = 'live';
        this.lastValid = f.t;
        const d = Math.abs(this.display);
        this.inTune = this.inTune ? d <= o.exitCents : d <= o.tolCents;
        if (this.inTune) this.confirmAcc += dt;
        else this.confirmAcc = Math.max(0, this.confirmAcc - 2 * dt);
        const lock = this.lock;
        if (this.confirmAcc >= o.confirmSec && lock.string != null && this.confirmedKey !== lock.key) {
          this.confirmedKey = lock.key;
          confirmed = lock.string;
        }
      } else if (this.state === 'live') this.lastValid = f.t; // stray frame: keep showing the current string
    } else if (this.state !== 'idle') {
      const quiet = f.t - this.lastValid;
      if (this.state === 'live' && quiet > 0.15) this.state = 'held';
      if (quiet > o.holdSec) {
        this.state = 'idle';
        this.display = null;
        this.buf = [];
        this.inTune = false;
      }
    }
    return this.reading(confirmed);
  }

  private relock(lock: Lock): void {
    this.lock = lock;
    this.buf = this.votes.filter((v) => v.lock.key === lock.key).map((v) => ({ t: v.t, c: v.c }));
    this.inTune = false;
    this.confirmAcc = 0;
    if (this.confirmedKey !== lock.key) this.confirmedKey = null;
  }

  private reading(confirmed: number | null): TunerReading {
    const lock = this.lock;
    const showing = this.state !== 'idle' && lock != null && this.display != null;
    const cents = showing ? this.display : null;
    return {
      state: showing ? this.state : 'idle',
      string: showing ? (lock as Lock).string : null,
      target: showing ? (lock as Lock).target : null,
      offString: showing ? (lock as Lock).string == null : false,
      cents,
      hz: showing && cents != null ? this.o.a4 * 2 ** (((lock as Lock).target + cents / 100 - 69) / 12) : null,
      inTune: showing && this.inTune,
      confirm: Math.min(1, this.confirmAcc / this.o.confirmSec),
      confirmed,
    };
  }
}
